import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShiftSettings = () => {
  const { t } = useTranslation();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: "",
    name: "Lunch",
    start_time: "12:00",
    end_time: "15:00",
    max_capacity: 50,
    available_times: "11:30,12:00,12:30,13:00,13:30,14:00"
  });

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await axios.get(`${API}/shifts`);
      setShifts(response.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
      toast.error("Error loading shifts");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const times = formData.available_times.split(',').map(t => t.trim());
      await axios.post(`${API}/shifts`, {
        ...formData,
        available_times: times,
        max_capacity: parseInt(formData.max_capacity)
      });
      
      toast.success("Shift created");
      setShowForm(false);
      setFormData({
        date: "",
        name: "Lunch",
        start_time: "12:00",
        end_time: "15:00",
        max_capacity: 50,
        available_times: "11:30,12:00,12:30,13:00,13:30,14:00"
      });
      fetchShifts();
    } catch (error) {
      console.error("Error creating shift:", error);
      toast.error("Error creating shift");
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="shift-settings-view">
      <div className="p-6 border-b border-white/10 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-gray-300" data-testid="shift-settings-title">{t('dashboard.shifts')}</h2>
          <p className="text-sm text-gray-300">Configure service times and capacity</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="bg-brand-gold text-gray-300 hover:bg-[#F0C74A]"
          data-testid="add-shift-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Shift
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {showForm && (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 mb-6" data-testid="shift-form">
            <h3 className="text-lg font-serif text-brand-beige mb-4">New Shift</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-300 block mb-1">Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  className="bg-[#222222] border-white/20"
                  data-testid="shift-date-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-300 block mb-1">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-[#222222] border-white/20"
                  data-testid="shift-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-300 block mb-1">Start Time</label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    required
                    className="bg-[#222222] border-white/20"
                    data-testid="shift-start-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 block mb-1">End Time</label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    required
                    className="bg-[#222222] border-white/20"
                    data-testid="shift-end-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-300 block mb-1">Max Capacity</label>
                <Input
                  type="number"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({...formData, max_capacity: e.target.value})}
                  required
                  className="bg-[#222222] border-white/20"
                  data-testid="shift-capacity-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-300 block mb-1">Available Times (comma-separated)</label>
                <Input
                  value={formData.available_times}
                  onChange={(e) => setFormData({...formData, available_times: e.target.value})}
                  placeholder="11:30,12:00,12:30"
                  required
                  className="bg-[#222222] border-white/20"
                  data-testid="shift-times-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-brand-gold text-gray-300" data-testid="shift-save-button">
                  Save Shift
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} data-testid="shift-cancel-button">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-gray-300">Loading...</div>
        ) : shifts.length === 0 ? (
          <div className="text-center py-8 text-gray-300" data-testid="no-shifts">No shifts configured yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shifts.map((shift, index) => (
              <div 
                key={shift.id} 
                className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4"
                data-testid={`shift-card-${index}`}
              >
                <h3 className="font-semibold text-gray-300 mb-2" data-testid={`shift-name-${index}`}>{shift.name}</h3>
                <p className="text-sm text-gray-300" data-testid={`shift-date-${index}`}>Date: {shift.date}</p>
                <p className="text-sm text-gray-300">Time: {shift.start_time} - {shift.end_time}</p>
                <p className="text-sm text-gray-300">Capacity: {shift.max_capacity}</p>
                <div className="mt-2 pt-2 border-t border-white/10">
                  <p className="text-xs text-gray-500">Available slots:</p>
                  <p className="text-sm text-gray-300">{shift.available_times?.join(', ')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftSettings;